import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const STATUS_TONE = {
    mapped: "text-emerald-300 border-emerald-400/30",
    shared: "text-sky-300 border-sky-400/30",
    hidden: "text-white/45 border-white/15",
};

function publishedText(p) {
    if (!p) return "hidden";
    return p.shared ? "shared" : p.field;
}

export default function FieldMapRow({ row, candidates, onSaved, readOnly }) {
    const [field, setField] = useState(row.confirmed_field || "");
    const [shared, setShared] = useState(!!row.shared);
    const [busy, setBusy] = useState(false);
    const key = `${row.database}-${row.collection}`;
    const dirty = (field || "") !== (row.confirmed_field || "") || shared !== !!row.shared;
    const save = async () => {
        if (row.status !== "hidden" && !shared && !field.trim()
            && !window.confirm(`Hide ${row.database}.${row.collection} from every tenant once published? Without a company field tenants cannot read it.`)) return;
        setBusy(true);
        try {
            const { data } = await api.put(`/admin/mongo-mcp/field-map/${encodeURIComponent(row.database)}/${encodeURIComponent(row.collection)}`,
                { confirmed_field: shared ? (field || null) : (field.trim() || null), shared });
            toast.success(`${row.database}.${row.collection} · ${data.status} (draft — publish to apply)`);
            onSaved(data);
        } catch (e) { toast.error(formatApiErrorDetail(e)); }
        finally { setBusy(false); }
    };
    const options = [...new Set([...(row.suggested || []), ...candidates])];
    return (
        <tr className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`field-map-row-${key}`}>
            <td className="px-4 py-2.5 font-mono text-[11px] text-white/60">{row.database}</td>
            <td className="px-4 py-2.5 font-mono text-xs text-white">{row.collection}</td>
            <td className="px-4 py-2.5 font-mono text-[11px] text-[#A78BFA]" data-testid={`field-map-suggested-${key}`}>
                {(row.suggested || []).join(", ") || <span className="text-white/25">—</span>}
            </td>
            <td className="px-4 py-2.5">
                {readOnly ? <span className="font-mono text-[11px] text-white/80" data-testid={`field-map-field-${key}`}>{row.confirmed_field || "—"}</span> : (
                    <>
                        <input value={field} onChange={(e) => setField(e.target.value)} disabled={shared} list={`fm-opts-${key}`}
                            placeholder={row.suggested?.[0] ? `confirm ${row.suggested[0]}` : "field name"} data-testid={`field-map-field-${key}`}
                            className="w-44 bg-[#050505] border border-white/10 rounded px-2 py-1 text-[11px] font-mono focus:outline-none focus:border-white/30 disabled:opacity-40" />
                        <datalist id={`fm-opts-${key}`}>{options.map((o) => <option key={o} value={o} />)}</datalist>
                    </>
                )}
            </td>
            <td className="px-4 py-2.5">
                <label className="flex items-center gap-1.5 text-[11px] text-white/60 cursor-pointer">
                    <input type="checkbox" checked={shared} disabled={readOnly} onChange={(e) => setShared(e.target.checked)} data-testid={`field-map-shared-${key}`} />
                    {shared ? "yes" : "no"}
                </label>
            </td>
            <td className="px-4 py-2.5 whitespace-nowrap">
                <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_TONE[row.status]}`}
                    data-testid={`field-map-status-${key}`}>{row.status}</span>
                {row.pending && (
                    <span className="ml-1.5 text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-amber-400/40 text-amber-200"
                        title={`Published: ${publishedText(row.published)}`} data-testid={`field-map-pending-${key}`}>unpublished</span>
                )}
            </td>
            <td className="px-4 py-2.5 text-right">
                {!readOnly && (
                    <button onClick={save} disabled={!dirty || busy} data-testid={`field-map-save-${key}`}
                        className="px-2.5 py-1 rounded text-[11px] font-medium bg-[#7C3AED] text-white hover:bg-[#6D28D9] disabled:bg-white/5 disabled:text-white/30 transition-colors">
                        {busy ? "Saving…" : "Save"}
                    </button>
                )}
            </td>
        </tr>
    );
}
